"use no memo";

import { useCallback, useLayoutEffect, useRef } from 'react';

/**
 * {@link https://react.dev/learn/separating-events-from-effects#declaring-an-effect-event React Docs}
 *
 * {@link https://chownow.atlassian.net/wiki/spaces/CE/pages/3883696137/React+Cookbook#useEffectEvent-%5BinlineCard%5D CN React Cookbook}
 *
 * creates an "Effect Event" -- these are "events" that are fired by an effect, but are not
 * themselves reactive to any values inside them. The effect itself is responsible for reactive values
 * and control when the event is fired.
 *
 */
export default function useEffectEvent<T extends (...args: never[]) => unknown>(
  cb: T
): (...args: Parameters<T>) => ReturnType<T> {
  const fn = useRef(cb);

  useLayoutEffect(() => {
    fn.current = cb;
  });

  return useCallback((...args: Parameters<T>) => {
    const cbInstance = fn.current;
    return cbInstance(...args) as ReturnType<T>;
  }, []);
}
